import React, { useCallback, useEffect, useState } from 'react';
import { planApi, formatDateTime, INTERVAL_LABELS } from './planApi';
import { ConfirmModal, Field } from './PlanUi';

/* ================================================================
   Billing automation (admin) — everything that makes self-serve
   plans run without a team: enforcement mode, free fallback plan,
   welcome trial, reminders, background jobs, rollout report and
   the one-time backfill for existing partners.
================================================================ */

const MODES = [
  ['off', 'Off — plans are not checked'],
  ['shadow', 'Shadow — allow everything, report what would be blocked'],
  ['enforce', 'Enforce — block according to each partner’s plan'],
];
const INTERVALS = Object.keys(INTERVAL_LABELS || { month: 1, quarter: 1, half_year: 1, year: 1 });
const toList = (s) => String(s || '').split(/[\s,]+/).map(Number).filter((n) => Number.isFinite(n) && n >= 0);

const BillingAutomation = ({ notify }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(7);
  const [jobs, setJobs] = useState([]);
  const [backfill, setBackfill] = useState(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, p, j] = await Promise.all([planApi.billingSettings(), planApi.listPlans(), planApi.billingJobs()]);
      setSettings(s.settings);
      setForm({
        mode: s.settings.enforcement.mode,
        tenantIsolation: s.settings.enforcement.tenantIsolation,
        readWhenLocked: s.settings.enforcement.readWhenLocked,
        freePlanId: s.settings.freePlan.planId || '',
        freeInterval: s.settings.freePlan.interval,
        trialEnabled: s.settings.welcomeTrial.enabled,
        trialPlanId: s.settings.welcomeTrial.planId || '',
        trialInterval: s.settings.welcomeTrial.interval,
        trialDays: s.settings.welcomeTrial.days,
        renewalDays: (s.settings.reminders.renewalDays || []).join(', '),
        trialEndingDays: (s.settings.reminders.trialEndingDays || []).join(', '),
        push: s.settings.reminders.push,
        email: s.settings.reminders.email,
        thresholds: (s.settings.usageAlertThresholds || []).join(', '),
        pendingHours: s.settings.pendingPaymentFallbackHours,
        schedulerEnabled: s.settings.scheduler.enabled,
        maintenanceMinutes: s.settings.scheduler.maintenanceMinutes,
        paymentReconcileMinutes: s.settings.scheduler.paymentReconcileMinutes,
        remindersMinutes: s.settings.scheduler.remindersMinutes,
        usageRecountMinutes: s.settings.scheduler.usageRecountMinutes ?? 60,
      });
      setPlans((p.plans || []).filter((x) => x.status === 'active'));
      setJobs(j.jobs || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try { setReport(await planApi.gateReport(days)); } catch (err) { notify?.(err.message, 'error'); }
  }, [days, notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await planApi.updateBillingSettings({
        enforcement: { mode: form.mode, tenantIsolation: form.tenantIsolation, readWhenLocked: !!form.readWhenLocked },
        freePlan: { planId: form.freePlanId || null, interval: form.freeInterval },
        welcomeTrial: { enabled: !!form.trialEnabled, planId: form.trialPlanId || null, interval: form.trialInterval, days: Number(form.trialDays) },
        reminders: { renewalDays: toList(form.renewalDays), trialEndingDays: toList(form.trialEndingDays), push: !!form.push, email: !!form.email },
        usageAlertThresholds: toList(form.thresholds).filter((n) => n >= 1 && n <= 100),
        pendingPaymentFallbackHours: Number(form.pendingHours),
        scheduler: {
          enabled: !!form.schedulerEnabled,
          maintenanceMinutes: Number(form.maintenanceMinutes),
          paymentReconcileMinutes: Number(form.paymentReconcileMinutes),
          remindersMinutes: Number(form.remindersMinutes),
          usageRecountMinutes: Number(form.usageRecountMinutes),
        },
      });
      setSettings(res.settings);
      notify?.('Billing settings saved');
    } catch (err) {
      const details = err.data?.details?.map((d) => `${(d.path || []).join('.')}: ${d.message}`).join(' · ');
      notify?.(details ? `${err.message} — ${details}` : err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const runJob = async (name) => {
    setBusy(name);
    try {
      const res = await planApi.runBillingJob(name);
      notify?.(res.started ? `${name} finished` : res.reason, res.started ? undefined : 'error');
      setJobs((await planApi.billingJobs()).jobs || []);
    } catch (err) { notify?.(err.message, 'error'); } finally { setBusy(''); }
  };

  const runBackfill = async (dryRun) => {
    setBusy(dryRun ? 'preview' : 'apply');
    try {
      const res = await planApi.backfill({ dryRun });
      setBackfill(res);
      if (!dryRun) notify?.(`Backfill done: ${res.report.processed} partners updated`);
    } catch (err) { notify?.(err.message, 'error'); } finally { setBusy(''); setConfirmApply(false); }
  };

  if (error) return <div className="pm-callout pm-callout--error">Could not load billing settings: {error} <button className="pm-btn pm-btn--ghost" onClick={load}>Retry</button></div>;
  if (!form) return <div className="pm-empty"><span className="pm-spinner" /> Loading…</div>;

  const envLocked = settings?.enforcement?.modeSource === 'env';
  const planOptions = plans.map((p) => <option key={p.planId} value={p.planId}>{p.displayName || p.name} ({p.planId})</option>);

  return (
    <div className="pm-form" style={{ gap: 24 }}>
      <section className="pm-card">
        <h3>Plan enforcement</h3>
        {envLocked && <div className="pm-callout pm-callout--warn">Mode is forced to <b>{settings.enforcement.mode}</b> by the PLAN_ENFORCEMENT server variable. Remove it to control the mode here.</div>}
        <Field label="Mode" hint="Start with Shadow, review the report below, then switch to Enforce.">
          <select className="pm-input" value={form.mode} onChange={set('mode')} disabled={envLocked}>
            {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Restaurant isolation" hint="Stops a partner from reading or changing another partner’s restaurant.">
          <select className="pm-input" value={form.tenantIsolation} onChange={set('tenantIsolation')}>
            {MODES.map(([v]) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <label className="pm-check"><input type="checkbox" checked={!!form.readWhenLocked} onChange={set('readWhenLocked')} /> Keep locked features viewable (read-only)</label>
      </section>

      <section className="pm-card">
        <h3>Free plan & welcome trial</h3>
        <Field label="Free plan (fallback)" hint="Partners move here when a trial or paid plan ends. Must have a ₹0 billing option.">
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="pm-input" value={form.freePlanId} onChange={set('freePlanId')}>
              <option value="">— none (partners are locked when a plan ends) —</option>
              {planOptions}
            </select>
            <select className="pm-input" value={form.freeInterval} onChange={set('freeInterval')}>
              {INTERVALS.map((i) => <option key={i} value={i}>{INTERVAL_LABELS?.[i] || i}</option>)}
            </select>
          </div>
        </Field>
        <label className="pm-check"><input type="checkbox" checked={!!form.trialEnabled} onChange={set('trialEnabled')} /> Give new partners a free trial</label>
        {form.trialEnabled && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Field label="Trial plan">
              <select className="pm-input" value={form.trialPlanId} onChange={set('trialPlanId')}>
                <option value="">— choose —</option>
                {planOptions}
              </select>
            </Field>
            <Field label="Billing interval after trial">
              <select className="pm-input" value={form.trialInterval} onChange={set('trialInterval')}>
                {INTERVALS.map((i) => <option key={i} value={i}>{INTERVAL_LABELS?.[i] || i}</option>)}
              </select>
            </Field>
            <Field label="Trial days"><input className="pm-input" type="number" min={1} max={365} value={form.trialDays} onChange={set('trialDays')} /></Field>
          </div>
        )}
        <Field label="Unpaid admin-assigned plan falls back after (hours)">
          <input className="pm-input" type="number" min={0} value={form.pendingHours} onChange={set('pendingHours')} />
        </Field>
      </section>

      <section className="pm-card">
        <h3>Reminders & alerts</h3>
        <Field label="Renewal reminders (days before expiry)" hint="e.g. 7, 3, 1"><input className="pm-input" value={form.renewalDays} onChange={set('renewalDays')} /></Field>
        <Field label="Trial-ending reminders (days before)"><input className="pm-input" value={form.trialEndingDays} onChange={set('trialEndingDays')} /></Field>
        <Field label="Usage alert thresholds (%)" hint="e.g. 80, 100"><input className="pm-input" value={form.thresholds} onChange={set('thresholds')} /></Field>
        <label className="pm-check"><input type="checkbox" checked={!!form.push} onChange={set('push')} /> Push to the partner app</label>
        <label className="pm-check"><input type="checkbox" checked={!!form.email} onChange={set('email')} /> Email</label>
      </section>

      <section className="pm-card">
        <h3>Background jobs</h3>
        <label className="pm-check"><input type="checkbox" checked={!!form.schedulerEnabled} onChange={set('schedulerEnabled')} /> Run automatically</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Field label="Subscription maintenance (min)"><input className="pm-input" type="number" min={1} value={form.maintenanceMinutes} onChange={set('maintenanceMinutes')} /></Field>
          <Field label="Payment re-check (min)"><input className="pm-input" type="number" min={1} value={form.paymentReconcileMinutes} onChange={set('paymentReconcileMinutes')} /></Field>
          <Field label="Reminders (min)"><input className="pm-input" type="number" min={5} value={form.remindersMinutes} onChange={set('remindersMinutes')} /></Field>
          <Field label="Usage recount (min)" hint="Re-checks every partner's stored usage"><input className="pm-input" type="number" min={10} max={1440} value={form.usageRecountMinutes} onChange={set('usageRecountMinutes')} /></Field>
        </div>
        <table className="pm-table">
          <thead><tr><th>Job</th><th>Last run</th><th>Took</th><th>Runs / failures</th><th>Last error</th><th /></tr></thead>
          <tbody>
            {jobs.length ? jobs.map((j) => (
              <tr key={j._id}>
                <td className="pm-mono">{j._id}</td>
                <td>{j.lastRunAt ? formatDateTime(j.lastRunAt) : '—'}</td>
                <td>{j.lastDurationMs != null ? `${j.lastDurationMs} ms` : '—'}</td>
                <td>{j.runs} / {j.failures}</td>
                <td style={{ color: j.lastError ? '#B5432F' : undefined }}>{j.lastError || '—'}</td>
                <td><button className="pm-btn pm-btn--ghost" disabled={busy === j._id} onClick={() => runJob(j._id)}>{busy === j._id ? 'Running…' : 'Run now'}</button></td>
              </tr>
            )) : <tr><td colSpan={6}>No runs yet — jobs start about 30 seconds after the server boots.</td></tr>}
          </tbody>
        </table>
      </section>

      <div><button className="pm-btn pm-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button></div>

      <section className="pm-card">
        <h3>Enforcement report</h3>
        <p className="pm-muted">Requests blocked (Enforce) or that would have been blocked (Shadow). Review before switching to Enforce.</p>
        <select className="pm-input" style={{ maxWidth: 160 }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 7, 14, 30].map((d) => <option key={d} value={d}>Last {d} day{d > 1 ? 's' : ''}</option>)}
        </select>
        {report && (
          <>
            {report.planConflicts?.length > 0 && (
              <div className="pm-callout pm-callout--warn">
                <strong>Plan settings to review</strong>
                <ul>
                  {report.planConflicts.map((v) => (
                    <li key={`${v.planId}@${v.version}`}>
                      <b>{v.name || v.planId} v{v.version}</b> ({v.subscribers} on it):
                      <ul>
                        {v.conflicts.map((c) => <li key={c.key} style={{ color: '#B5432F' }}>{c.msg}</li>)}
                        {v.warnings.map((w) => <li key={w.key}>{w.msg}</li>)}
                      </ul>
                    </li>
                  ))}
                </ul>
                <span className="pm-small pm-muted">Published versions can't change. Fix it in a new draft, publish, then move subscribers to the new version.</span>
              </div>
            )}
            <table className="pm-table">
              <thead><tr><th>Reason</th><th>Mode</th><th>Events</th><th>Partners</th></tr></thead>
              <tbody>{report.byCode.length ? report.byCode.map((r) => <tr key={`${r.code}-${r.mode}`}><td className="pm-mono">{r.code}</td><td>{r.mode}</td><td>{r.events}</td><td>{r.owners}</td></tr>) : <tr><td colSpan={4}>Nothing blocked in this period.</td></tr>}</tbody>
            </table>
            {report.byRoute.length > 0 && (
              <table className="pm-table">
                <thead><tr><th>API</th><th>Reason</th><th>Events</th><th>Partners</th></tr></thead>
                <tbody>{report.byRoute.map((r) => <tr key={`${r.route}-${r.code}`}><td className="pm-mono">{r.route}</td><td>{r.code}</td><td>{r.events}</td><td>{r.owners}</td></tr>)}</tbody>
              </table>
            )}
          </>
        )}
      </section>

      <section className="pm-card">
        <h3>Existing partners</h3>
        <p className="pm-muted">Gives every restaurant owner without an active plan the welcome trial (never subscribed) or the free plan. Preview first.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="pm-btn pm-btn--ghost" onClick={() => runBackfill(true)} disabled={!!busy}>{busy === 'preview' ? 'Checking…' : 'Preview'}</button>
          <button className="pm-btn pm-btn--primary" onClick={() => setConfirmApply(true)} disabled={!!busy || !backfill?.dryRun || !backfill.report.toProcess}>Apply</button>
        </div>
        {backfill && (
          <>
            <p>{backfill.dryRun ? 'Preview' : 'Result'}: {backfill.report.owners} partners · {backfill.report.alreadyActive} already on a plan · {backfill.dryRun ? `${backfill.report.toProcess} would be updated` : `${backfill.report.processed} updated, ${backfill.report.failed} failed`}</p>
            {backfill.report.warning && <div className="pm-callout pm-callout--warn">{backfill.report.warning}</div>}
            {backfill.report.details.length > 0 && (
              <table className="pm-table">
                <thead><tr><th>Partner</th><th>Current</th><th>{backfill.dryRun ? 'Will get' : 'Now'}</th></tr></thead>
                <tbody>{backfill.report.details.map((d) => <tr key={d.ownerId}><td>{d.email}</td><td>{d.current || '—'}</td><td>{d.action || d.now || d.error}</td></tr>)}</tbody>
              </table>
            )}
          </>
        )}
      </section>

      {confirmApply && (
        <ConfirmModal
          title="Assign plans to existing partners?"
          message={`${backfill?.report?.toProcess || 0} partners will get their welcome trial or the free plan. Partners already on a plan are not touched.`}
          confirmLabel="Assign plans"
          busy={busy === 'apply'}
          onConfirm={() => runBackfill(false)}
          onClose={() => setConfirmApply(false)}
        />
      )}
    </div>
  );
};

export default BillingAutomation;
